/**
 * Record Offline Transaction Dialog
 * For recording manual bank transactions (deposits, withdrawals, expenses)
 * Phase 7: Bank Transaction Management
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, MenuItem, Grid, Typography, Box, Alert,
  CircularProgress, InputAdornment
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { BankTransactionAPI } from '../../../../services/bankTransactionService';

const EXPENSE_CATEGORIES = [
  'Rent', 'Salaries', 'Utilities', 'Office Supplies', 'Marketing',
  'Travel', 'Software', 'Professional Services', 'Insurance', 'Other'
];

const RecordOfflineTransactionDialog = ({ open, onClose, onSuccess, bankAccounts = [] }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [transactionTypes, setTransactionTypes] = useState([]);

  const [formData, setFormData] = useState({
    bankAccountId: '',
    transactionType: '',
    amount: '',
    externalReference: '',
    description: '',
    category: '',
    proofDocumentPath: '',
    transactionDate: new Date()
  });

  useEffect(() => {
    if (open) {
      loadTransactionTypes();
    }
  }, [open]);

  const loadTransactionTypes = async () => {
    try {
      const types = await BankTransactionAPI.getTransactionTypes();
      setTransactionTypes(types);
    } catch (err) {
      console.error('Error loading transaction types:', err);
      // Fallback types
      setTransactionTypes([
        { value: 'MANUAL_DEPOSIT', label: 'Manual Deposit', direction: 'CREDIT' },
        { value: 'MANUAL_WITHDRAWAL', label: 'Manual Withdrawal', direction: 'DEBIT' },
        { value: 'EXPENSE', label: 'Expense', direction: 'DEBIT' },
        { value: 'DIRECT_TRANSFER_IN', label: 'Direct Transfer In', direction: 'CREDIT' },
        { value: 'DIRECT_TRANSFER_OUT', label: 'Direct Transfer Out', direction: 'DEBIT' },
        { value: 'BANK_CHARGES', label: 'Bank Charges', direction: 'DEBIT' },
        { value: 'BANK_INTEREST', label: 'Bank Interest', direction: 'CREDIT' }
      ]);
    }
  };

  const handleChange = (field) => (event) => {
    setFormData({ ...formData, [field]: event.target.value });
    setError('');
  };

  const handleDateChange = (date) => {
    setFormData({ ...formData, transactionDate: date });
  };

  const handleSubmit = async () => {
    if (!formData.bankAccountId || !formData.transactionType || !formData.amount) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await BankTransactionAPI.recordOfflineTransaction({
        ...formData,
        amount: parseFloat(formData.amount),
        transactionDate: formData.transactionDate?.toISOString()
      });

      onSuccess?.(result);
      onClose();
      resetForm();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to record transaction');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      bankAccountId: '',
      transactionType: '',
      amount: '',
      externalReference: '',
      description: '',
      category: '',
      proofDocumentPath: '',
      transactionDate: new Date()
    });
    setError('');
  };

  const selectedType = transactionTypes.find(t => t.value === formData.transactionType);
  const isDebit = selectedType?.direction === 'DEBIT';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6">Record Offline Transaction</Typography>
        <Typography variant="caption" color="textSecondary">
          Manual bank transactions require verification for amounts ≥ 1,000 AED
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              select
              fullWidth
              label="Bank Account *"
              value={formData.bankAccountId}
              onChange={handleChange('bankAccountId')}
            >
              {bankAccounts.map(acc => (
                <MenuItem key={acc.id} value={acc.id}>
                  {acc.accountName} - {acc.bankName}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <TextField
              select
              fullWidth
              label="Transaction Type *"
              value={formData.transactionType}
              onChange={handleChange('transactionType')}
            >
              {transactionTypes.map(type => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label} ({type.direction})
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Amount *"
              type="number"
              value={formData.amount}
              onChange={handleChange('amount')}
              InputProps={{
                startAdornment: <InputAdornment position="start">AED</InputAdornment>
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DateTimePicker
                label="Transaction Date"
                value={formData.transactionDate}
                onChange={handleDateChange}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Reference Number"
              value={formData.externalReference}
              onChange={handleChange('externalReference')}
              placeholder="Cheque #, Bank Ref, etc."
            />
          </Grid>

          {isDebit && (
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Category"
                value={formData.category}
                onChange={handleChange('category')}
              >
                {EXPENSE_CATEGORIES.map(cat => (
                  <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                ))}
              </TextField>
            </Grid>
          )}

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Description"
              value={formData.description}
              onChange={handleChange('description')}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Proof Document Path"
              value={formData.proofDocumentPath}
              onChange={handleChange('proofDocumentPath')}
              placeholder="/uploads/bank-slips/..."
              helperText="Path to uploaded bank slip or proof document"
            />
          </Grid>
        </Grid>

        {formData.amount && parseFloat(formData.amount) >= 1000 && (
          <Box mt={2}>
            <Alert severity="info">
              Transactions ≥ 1,000 AED require verification before balance is updated
            </Alert>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color={isDebit ? 'error' : 'success'}
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : `Record ${isDebit ? 'Debit' : 'Credit'}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RecordOfflineTransactionDialog;
